import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { FiAlertCircle, FiBriefcase, FiCheckCircle, FiClock, FiInfo, FiLoader, FiMail, FiMessageSquare, FiSend, FiZap } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { getUserKey } from '../utils/userStorage';

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE  || '';
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE || '';
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_KEY      || '';

const load = (key, def) => {
  try { const s = localStorage.getItem(key); return s !== null ? JSON.parse(s) : def; } catch { return def; }
};

const SUBJECTS = [
  "Umumiy savol",
  "Texnik muammo",
  "Hamkorlik taklifi",
  "Xizmat haqida",
  "Boshqa",
];

export default function ContactUs() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const formRef = useRef(null);

  const adminEmail = load('smm_crm_email', 'razzoqovshaxram2@gmail.com');
  const adminName  = load('smm_crm_name',  'Admin');

  const [fromName,  setFromName]  = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [subject,   setSubject]   = useState('');
  const [message,   setMessage]   = useState('');
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [toast,     setToast]     = useState(null);

  const accentRaw  = load(getUserKey('accent'), 'blue');
  const accentColor = { blue:'#3b82f6', purple:'#8b5cf6', green:'#22c55e', orange:'#f97316' }[accentRaw] || '#3b82f6';

  const bg   = isDark ? '#0f172a' : '#f1f5f9';
  const card = isDark ? '#1e293b' : '#ffffff';
  const bord = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const tp   = isDark ? '#f8fafc' : '#0f172a';
  const ts   = isDark ? '#94a3b8' : '#64748b';
  const ibg  = isDark ? '#0f172a' : '#f8fafc';
  const ibrd = isDark ? '#334155' : '#e2e8f0';

  const inp = {
    width: '100%', padding: '13px 16px', borderRadius: '14px',
    border: `1.5px solid ${ibrd}`, background: ibg, color: tp,
    fontSize: '15px', outline: 'none', boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif', transition: 'border-color 0.2s',
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromName.trim() || !fromEmail.trim() || !subject || !message.trim()) {
      showToast("Barcha maydonlarni to'ldiring!", 'error'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fromEmail.trim())) {
      showToast("Email manzili noto'g'ri!", 'error'); return;
    }

    setSending(true);
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name:   fromName.trim(),
          from_email:  fromEmail.trim(),
          subject,
          message:     message.trim(),
          admin_email: adminEmail,
          admin_name:  adminName,
        },
        { publicKey: PUBLIC_KEY }
      );
      setSent(true);
      setFromName(''); setFromEmail(''); setSubject(''); setMessage('');
      showToast("Xabar muvaffaqiyatli yuborildi!");
    } catch {
      showToast("Xabar yuborishda xato! Qayta urinib ko'ring.", 'error');
    } finally {
      setSending(false);
    }
  };

  const CONTACT_INFO = [
    { icon: FiMail, label: 'Email', val: adminEmail, color: '#3b82f6' },
    { icon: FiBriefcase, label: 'Platform', val: 'SMM CRM v2.0', color: '#8b5cf6' },
    { icon: FiClock, label: 'Ish vaqti', val: 'Dush–Jum, 9:00–18:00', color: '#22c55e' },
    { icon: FiZap, label: 'Javob vaqti', val: '24 soat ichida', color: '#f97316' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: bg, padding: '24px', color: tp, fontFamily: 'Inter, sans-serif', boxSizing: 'border-box' }}>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key="toast"
            initial={{ opacity: 0, y: -20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 99999,
              padding: '13px 24px', borderRadius: '14px',
              background: toast.type === 'error' ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
              color: '#fff', fontWeight: 700, fontSize: '14px',
              boxShadow: '0 16px 40px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            {toast.type === 'error' ? <FiAlertCircle size={17} /> : <FiCheckCircle size={17} />} {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '20px', background: accentColor + '20', fontSize: '30px', marginBottom: '16px' }}>
            <FiMessageSquare size={28} strokeWidth={1.7} />
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: '30px', fontWeight: 800 }}>Biz bilan bog'laning</h1>
          <p style={{ margin: 0, color: ts, fontSize: '15px' }}>Savol yoki takliflaringiz bo'lsa, xabar qoldiring</p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '24px', alignItems: 'flex-start' }}>

          {/* ── Left: Form ── */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 }}
            style={{ borderRadius: '24px', background: card, border: `1px solid ${bord}`, padding: '36px',
              boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.06)' }}>

            <AnimatePresence mode="wait">
              {sent ? (
                /* Success state */
                <motion.div key="success"
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                    style={{ width: '80px', height: '80px', borderRadius: '24px', background: '#22c55e20', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <FiCheckCircle size={38} strokeWidth={1.7} />
                  </motion.div>
                  <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700 }}>Xabar yuborildi!</h2>
                  <p style={{ margin: '0 0 28px', color: ts, lineHeight: 1.6 }}>
                    Xabaringiz muvaffaqiyatli yetkazildi. Tez orada javob beramiz.
                  </p>
                  <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={() => setSent(false)} type="button"
                    style={{ padding: '13px 28px', borderRadius: '14px', border: 'none', background: accentColor, color: '#fff', fontWeight: 700, fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Yana xabar yozish
                  </motion.button>
                </motion.div>
              ) : (
                /* Form */
                <motion.form key="form" ref={formRef} onSubmit={handleSubmit}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '13px', background: accentColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>✍️</div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Xabar yuborish</h2>
                      <p style={{ margin: '2px 0 0', fontSize: '13px', color: ts }}>Barcha maydonlarni to'ldiring</p>
                    </div>
                  </div>

                  {/* Name + Email row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: ts, marginBottom: '7px' }}>Ismingiz</label>
                      <input type="text" value={fromName} onChange={e => setFromName(e.target.value)}
                        placeholder="Shaxram Razzoqov" style={inp}
                        onFocus={e => e.target.style.borderColor = accentColor}
                        onBlur={e => e.target.style.borderColor = ibrd} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: ts, marginBottom: '7px' }}>Email</label>
                      <input type="email" value={fromEmail} onChange={e => setFromEmail(e.target.value)}
                        placeholder="email@gmail.com" style={inp}
                        onFocus={e => e.target.style.borderColor = accentColor}
                        onBlur={e => e.target.style.borderColor = ibrd} />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: ts, marginBottom: '7px' }}>Mavzu</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {SUBJECTS.map(s => (
                        <motion.button key={s} type="button" whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                          onClick={() => setSubject(s)}
                          style={{ padding: '6px 14px', borderRadius: '20px', border: `1.5px solid ${subject === s ? accentColor : ibrd}`, background: subject === s ? accentColor + '15' : ibg, color: subject === s ? accentColor : ts, fontWeight: subject === s ? 700 : 500, fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit' }}>
                          {s}
                        </motion.button>
                      ))}
                    </div>
                    <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                      placeholder="Yoki o'zingiz yozing..." style={inp}
                      onFocus={e => e.target.style.borderColor = accentColor}
                      onBlur={e => e.target.style.borderColor = ibrd} />
                  </div>

                  {/* Message */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: ts, marginBottom: '7px' }}>
                      Xabar matni
                      <span style={{ float: 'right', fontWeight: 400, color: message.length > 800 ? '#ef4444' : ts }}>{message.length}/1000</span>
                    </label>
                    <textarea value={message} onChange={e => setMessage(e.target.value.slice(0, 1000))}
                      placeholder="Xabaringizni bu yerga yozing..."
                      rows={6}
                      style={{ ...inp, resize: 'vertical', minHeight: '140px', lineHeight: 1.6 }}
                      onFocus={e => e.target.style.borderColor = accentColor}
                      onBlur={e => e.target.style.borderColor = ibrd} />
                  </div>

                  <motion.button type="submit" whileHover={{ scale: sending ? 1 : 1.02 }} whileTap={{ scale: sending ? 1 : 0.97 }}
                    disabled={sending}
                    style={{ padding: '15px', borderRadius: '14px', border: 'none',
                      background: sending ? '#94a3b8' : `linear-gradient(135deg,${accentColor},${accentColor}cc)`,
                      color: '#fff', fontWeight: 700, fontSize: '16px', cursor: sending ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                      boxShadow: sending ? 'none' : `0 8px 24px ${accentColor}44`, transition: 'all 0.2s' }}>
                    {sending ? (
                      <><FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Yuborilmoqda...</>
                    ) : (
                      <><FiSend size={16} /> Xabar Yuborish</>
                    )}
                  </motion.button>

                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ── Right: Info cards ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Contact info cards */}
            {CONTACT_INFO.map((item, i) => {
              const ContactIcon = item.icon;
              return (
                <motion.div key={item.label}
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}
                  style={{ borderRadius: '18px', background: card, border: `1px solid ${bord}`, padding: '18px 20px',
                    boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.05)',
                    display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: item.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                    <ContactIcon size={20} strokeWidth={1.8} aria-hidden="true" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px', fontSize: '12px', fontWeight: 600, color: ts }}>{item.label}</p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: tp, wordBreak: 'break-all' }}>{item.val}</p>
                  </div>
                </motion.div>
              );
            })}

            {/* Note card */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.34 }}
              style={{ borderRadius: '18px', background: accentColor + '12', border: `1.5px solid ${accentColor}30`, padding: '20px',
                boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.05)' }}>
              <p style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: accentColor, display: 'flex', alignItems: 'center', gap: '7px' }}><FiInfo size={15} /> Eslatma</p>
              <p style={{ margin: 0, fontSize: '13px', color: ts, lineHeight: 1.7 }}>
                Xabaringizni qabul qilib, 24 soat ichida javob beramiz. Shoshilinch holatlar uchun to'g'ridan-to'g'ri email orqali murojaat qiling.
              </p>
            </motion.div>

          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
